import type {
  AppSurface,
  BillingUnit,
  HeroAppId,
  Language,
  Product,
  ProductPurchase,
} from '@hero/schema';
import { businessSchema } from '@hero/schema';
import type { z } from 'zod';
import { contact } from './contact.config';

/**
 * Brand id (`hero` | `wathiqa_plus`).
 *
 * Local alias — `@hero/schema` also exports a legacy Org `Business` type,
 * so importing `Business` from the barrel is ambiguous.
 */
export type Business = z.infer<typeof businessSchema>;

/**
 * App registry — every Hero app, every business, and every audience
 * product under those businesses, keyed by persisted ids.
 *
 * - {@link BUSINESS_REGISTRY} — brand facts (name, website, credit noun).
 * - {@link APP_REGISTRY} — deployable → business + surface.
 * - {@link PRODUCT_REGISTRY} — audience offering under a business
 *   (`hero_enterprise`, …) with allowed units and plan codes.
 *
 * Seat vs order is `BillingUnit` on `ProductPurchase`, not a separate
 * catalog id. Commercial identity is the shared `productPurchaseSchema`.
 */

/** Per-brand facts. */
export interface BusinessEntry {
  readonly displayName: Record<Language, string>;
  readonly website: string;
  readonly phone?: string;
  readonly hosting?: 'external_base44';
  /**
   * What one prepaid credit buys — the meter, and the display noun.
   *
   * `order` spends when the order is created: Hero sells the platform that
   * runs the shipment, and the shipment exists from the moment it is
   * published whether or not a government document is ever filed against it.
   *
   * `waybill` spends when the document is issued, because the document IS
   * the product. Charging at create time meant a Wathiqa Plus customer whose
   * filing was refused — a bad plate, an ELM outage — paid for a وثيقة they
   * never received. See {@link creditSpendsOnIssuance}.
   */
  readonly creditUnit: 'order' | 'waybill';
  /**
   * Public brand mark (HTTPS). Used for WhatsApp template image headers
   * and other surfaces that need a stable logo URL — not an env secret.
   */
  readonly logoUrl?: string;
}

export type UnitPricing = 'seat_recurring' | 'credit_ladder' | 'one_time_flat';

/** Per-audience-product facts. */
export interface ProductEntry {
  readonly business: Business;
  /** Maps to former SubscriptionAudience. */
  readonly audience: 'org' | 'freelancer';
  readonly allowedUnits: readonly BillingUnit[];
  /**
   * Plan codes sellable on this product (any unit). Unit selects pricing
   * path at purchase time — seat plans vs credit plans are filtered by
   * which unit is chosen, via {@link plansForProductUnit}.
   */
  readonly planCodes: readonly string[];
  readonly displayName: Record<Language, string>;
}

/** Per-deployable facts. */
export interface AppEntry {
  readonly business: Business;
  readonly surface: AppSurface;
  readonly url?: string;
  readonly ios?: string;
  readonly android?: string;
  readonly isAgentSurface?: boolean;
  readonly clientSettable: boolean;
}

/**
 * Plan codes that meter as vehicle seats (period / seat_recurring).
 * Everything else in the catalog is order-credit / usage.
 */
export const VEHICLE_PLAN_CODES = [
  'free',
  'starter',
  'growth',
  'custom',
] as const;

/**
 * Wathiqa Plus seat plans. Kept apart from {@link VEHICLE_PLAN_CODES}
 * because the Hero products spread that list into their `planCodes`, and
 * a Wathiqa plan listed on a Hero product would resolve to the Hero brand.
 */
export const WATHIQA_VEHICLE_PLAN_CODES = ['wathiqa_unlimited'] as const;

export const ORDER_PLAN_CODES = [
  'start',
  'pro',
  'max',
  'wathiqa_start',
  'wathiqa_pro',
  'wathiqa_max',
] as const;

export const BUSINESS_REGISTRY = {
  hero: {
    displayName: { en: 'Hero AI', ar: 'Hero AI', ur: 'Hero AI' },
    website: 'https://www.tryhero.io',
    phone: contact.customerCarePhone,
    creditUnit: 'order',
  },
  wathiqa_plus: {
    displayName: { en: 'Wathiqa Plus', ar: 'وثيقة بلس', ur: 'وثيقة بلس' },
    website: 'https://wathiqaplus.com',
    hosting: 'external_base44',
    creditUnit: 'waybill',
    logoUrl:
      'https://res.cloudinary.com/dsze9ffpl/image/upload/v1789335619/wathiqa-plus-logo_swv0ib.png',
  },
} as const satisfies Record<Business, BusinessEntry>;

export const PRODUCT_REGISTRY = {
  hero_enterprise: {
    business: 'hero',
    audience: 'org',
    allowedUnits: ['vehicle', 'order'],
    planCodes: [...VEHICLE_PLAN_CODES, 'start', 'pro', 'max'],
    displayName: {
      en: 'Hero Enterprise',
      ar: 'Hero للمؤسسات',
      ur: 'Hero برائے ادارے',
    },
  },
  hero_freelancer: {
    business: 'hero',
    audience: 'freelancer',
    allowedUnits: ['vehicle', 'order'],
    planCodes: [...VEHICLE_PLAN_CODES, 'start', 'pro', 'max'],
    displayName: {
      en: 'Hero Freelancer',
      ar: 'Hero للأفراد',
      ur: 'Hero برائے افراد',
    },
  },
  wathiqa_plus_enterprise: {
    business: 'wathiqa_plus',
    audience: 'org',
    allowedUnits: ['vehicle', 'order'],
    planCodes: [...WATHIQA_VEHICLE_PLAN_CODES, 'wathiqa_start', 'wathiqa_pro', 'wathiqa_max'],
    displayName: {
      en: 'Wathiqa Plus Enterprise',
      ar: 'وثيقة بلس للمؤسسات',
      ur: 'وثيقة بلس برائے ادارے',
    },
  },
  wathiqa_plus_freelancer: {
    business: 'wathiqa_plus',
    audience: 'freelancer',
    allowedUnits: ['vehicle', 'order'],
    planCodes: [...WATHIQA_VEHICLE_PLAN_CODES, 'wathiqa_start', 'wathiqa_pro', 'wathiqa_max'],
    displayName: {
      en: 'Wathiqa Plus Freelancer',
      ar: 'وثيقة بلس للأفراد',
      ur: 'وثيقة بلس برائے افراد',
    },
  },
} as const satisfies Record<Product, ProductEntry>;

export const APP_REGISTRY = {
  dashboard: {
    business: 'hero',
    surface: 'web',
    url: 'https://app.tryhero.io',
    clientSettable: true,
  },
  carrier_app: {
    business: 'hero',
    surface: 'mobile',
    ios: 'https://apps.apple.com/sa/app/hero-fleet/id6740289326',
    android: 'https://play.google.com/store/apps/details?id=com.hero.carrier',
    clientSettable: true,
  },
  customer_app: {
    business: 'hero',
    surface: 'mobile',
    ios: 'https://apps.apple.com/us/app/hero-%D9%87%D9%8A%D8%B1%D9%88/id6605928674',
    android: 'https://play.google.com/store/apps/details?id=com.hero.customerapp',
    clientSettable: true,
  },
  admin_panel: { business: 'hero', surface: 'admin', clientSettable: true },
  landing: {
    business: 'hero',
    surface: 'web',
    url: 'https://www.tryhero.io',
    clientSettable: true,
  },
  hero_agent: {
    business: 'hero',
    surface: 'whatsapp',
    isAgentSurface: true,
    clientSettable: false,
  },
  wathiqa_plus_agent: {
    business: 'wathiqa_plus',
    surface: 'whatsapp',
    isAgentSurface: true,
    clientSettable: false,
  },
} as const satisfies Record<HeroAppId, AppEntry>;

export const appEntry = (app: HeroAppId): AppEntry => APP_REGISTRY[app];

export const businessEntry = (business: Business): BusinessEntry =>
  BUSINESS_REGISTRY[business];

export const productEntry = (product: Product): ProductEntry =>
  PRODUCT_REGISTRY[product];

export type ClientSettableAppId = {
  [K in HeroAppId]: (typeof APP_REGISTRY)[K]['clientSettable'] extends true
    ? K
    : never;
}[HeroAppId];

export const appBusiness = (app: HeroAppId): Business => APP_REGISTRY[app].business;

export const appSurface = (app: HeroAppId): AppSurface => APP_REGISTRY[app].surface;

export const appBrand = (app: HeroAppId): BusinessEntry =>
  businessEntry(appBusiness(app));

export const isClientSettableApp = (app: HeroAppId): boolean =>
  APP_REGISTRY[app].clientSettable;

export const isAgentSurfaceApp = (app: HeroAppId): boolean =>
  appEntry(app).isAgentSurface === true;

export const businessDisplayName = (
  business: Business,
  language: Language,
): string => BUSINESS_REGISTRY[business].displayName[language];

export const businessWebsite = (business: Business): string =>
  BUSINESS_REGISTRY[business].website;

export const businessLogoUrl = (business: Business): string | undefined =>
  businessEntry(business).logoUrl;


export const businessCreditUnit = (business: Business): 'order' | 'waybill' =>
  BUSINESS_REGISTRY[business].creditUnit;

/**
 * Whether this business bills the document rather than the order — the one
 * answer to "when is a credit owed?".
 *
 * Every surface has to agree on this or the same shipment is charged twice or
 * not at all: the Hero WhatsApp agent, the Wathiqa Plus WhatsApp agent, the
 * in-app chat and the dashboard button all create orders through the same
 * pipeline and file documents through the same issuance path, so both of those
 * read this instead of deciding for themselves.
 *
 * Defaults to `hero` for the legacy orders written before `Order.business`
 * existed — those predate Wathiqa Plus entirely, so order-metering is the
 * accurate answer for them and not merely the safe one.
 */
export const creditSpendsOnIssuance = (business: Business | undefined): boolean =>
  businessCreditUnit(business ?? 'hero') === 'waybill';


export const businessWhatsAppLink = (
  business: Business,
  text?: string,
): string | undefined => {
  const digits = businessEntry(business).phone?.replace(/\D/g, '');
  if (!digits) return undefined;
  return text
    ? `https://wa.me/${digits}?text=${encodeURIComponent(text)}`
    : `https://wa.me/${digits}`;
};


export const productsForBusiness = (business: Business): Product[] =>
  (Object.keys(PRODUCT_REGISTRY) as Product[]).filter(
    p => PRODUCT_REGISTRY[p].business === business,
  );

export const productBusiness = (product: Product): Business =>
  PRODUCT_REGISTRY[product].business;

export const productAudience = (product: Product): 'org' | 'freelancer' =>
  PRODUCT_REGISTRY[product].audience;

export const productDisplayNameForProduct = (
  product: Product,
  language: Language,
): string => PRODUCT_REGISTRY[product].displayName[language];

export const isVehiclePlanCode = (planCode: string): boolean =>
  (VEHICLE_PLAN_CODES as readonly string[]).includes(planCode) ||
  (WATHIQA_VEHICLE_PLAN_CODES as readonly string[]).includes(planCode);

export const planUnit = (planCode: string): BillingUnit =>
  isVehiclePlanCode(planCode) ? 'vehicle' : 'order';

export const unitPricing = (unit: BillingUnit): UnitPricing =>
  unit === 'vehicle' ? 'seat_recurring' : 'credit_ladder';

export const plansForProductUnit = (
  product: Product,
  unit: BillingUnit,
): readonly string[] =>
  PRODUCT_REGISTRY[product].planCodes.filter(code => planUnit(code) === unit);

/** Plan code → owning Product (first product that lists it). */
const PLAN_PRODUCT_INDEX: Readonly<Record<string, Product>> = Object.freeze(
  (Object.keys(PRODUCT_REGISTRY) as Product[]).reduce<Record<string, Product>>(
    (index, product) => {
      for (const planCode of PRODUCT_REGISTRY[product].planCodes) {
        if (!(planCode in index)) index[planCode] = product;
      }
      return index;
    },
    {},
  ),
);

/**
 * Resolve a catalog Product for a plan + audience.
 * Freelancer plans share codes with enterprise; pick by audience.
 */
export const getPlanProduct = (
  planCode: string,
  audience: 'org' | 'freelancer' = 'org',
): Product | undefined => {
  const candidates = (Object.keys(PRODUCT_REGISTRY) as Product[]).filter(p => {
    const entry = PRODUCT_REGISTRY[p];
    return (
      (entry.planCodes as readonly string[]).includes(planCode) &&
      entry.audience === audience
    );
  });
  return candidates[0] ?? PLAN_PRODUCT_INDEX[planCode];
};

export const getPlanBusiness = (planCode: string): Business | undefined => {
  const product = PLAN_PRODUCT_INDEX[planCode];
  return product ? PRODUCT_REGISTRY[product].business : undefined;
};

export const productForAudience = (
  business: Business,
  audience: 'org' | 'freelancer',
): Product => {
  const match = productsForBusiness(business).find(
    p => PRODUCT_REGISTRY[p].audience === audience,
  );
  if (!match) {
    throw new Error(`No product for business=${business} audience=${audience}`);
  }
  return match;
};

export const assertPurchaseValid = (purchase: ProductPurchase): void => {
  const entry = PRODUCT_REGISTRY[purchase.product];
  if (entry.business !== purchase.business) {
    throw new Error(
      `purchase.product ${purchase.product} belongs to ${entry.business}, not ${purchase.business}`,
    );
  }
  if (!(entry.allowedUnits as readonly BillingUnit[]).includes(purchase.unit)) {
    throw new Error(
      `unit ${purchase.unit} not allowed on product ${purchase.product}`,
    );
  }
  if (!(entry.planCodes as readonly string[]).includes(purchase.planCode)) {
    throw new Error(
      `planCode ${purchase.planCode} not on product ${purchase.product}`,
    );
  }
  if (planUnit(purchase.planCode) !== purchase.unit) {
    throw new Error(
      `planCode ${purchase.planCode} is ${planUnit(purchase.planCode)}, purchase.unit is ${purchase.unit}`,
    );
  }
};

export const buildPurchase = (input: {
  business: Business;
  audience: 'org' | 'freelancer';
  planCode: string;
  unit?: BillingUnit;
  addonCode?: string;
}): ProductPurchase => {
  const unit = input.unit ?? planUnit(input.planCode);
  const product = productForAudience(input.business, input.audience);
  const purchase: ProductPurchase = {
    business: input.business,
    product,
    planCode: input.planCode,
    unit,
    ...(input.addonCode ? { addonCode: input.addonCode } : {}),
  };
  assertPurchaseValid(purchase);
  return purchase;
};

/** Order / funnel helper: never silent-default to hero when unknown. */
export const orderBusiness = (order: {
  business?: Business;
  doc?: { app?: HeroAppId };
}): Business | undefined =>
  order.business ?? (order.doc?.app ? appBusiness(order.doc.app) : undefined);
